import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { UserPlus, Users, FileText, Package, CalendarDays, ClipboardEdit, BarChart3, Trophy, SlidersHorizontal, Download, Search, Upload, Check, Sparkles, ShieldCheck, ScrollText } from "lucide-react";
import DutyRoster from "@/components/staff/DutyRoster";
import DailyReport from "@/components/staff/DailyReport";
import PerformanceDashboard from "@/components/staff/PerformanceDashboard";

// ---- Stable, hoisted text field. Defining fields inside a parent's render
//      function makes React treat them as a new component every render and
//      unmounts the <Input>, killing focus after each keystroke.
function TxtField({ f, upd, k, label, type = "text", cols = 1, grammar = false, testid }) {
  return (
    <div className={cols === 2 ? "col-span-2" : ""}>
      <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>
        {label}
        {grammar && <span className="ml-1 text-[9px] normal-case tracking-normal text-[#B4841F]">· auto-fix on blur</span>}
      </label>
      <Input data-testid={testid || `sd-${k}`} type={type} value={f[k] ?? ""}
        onChange={e => upd(k, e.target.value)}
        onBlur={grammar ? (e => runGrammarFix(k, e.target.value, upd, k)) : undefined} />
    </div>
  );
}

// ---- Helpers ----
// Debounced grammar auto-correct on field blur. Only runs on text long enough
// to be a sentence/phrase (>= 6 chars). Silently fails to the original value.
async function runGrammarFix(fieldKey, value, upd, field) {
  const text = (value || "").trim();
  if (text.length < 6) return;
  // Skip if it's obviously a code/number/single token
  if (!/\s/.test(text) && text.length < 12) return;
  try {
    const r = await api.post("/tools/grammar-fix", { text, field: field || fieldKey });
    const fixed = (r.data?.fixed || "").trim();
    if (fixed && fixed !== text) {
      upd(fieldKey, fixed);
      toast.success("Auto-corrected", { description: `${fieldKey}: “${fixed.slice(0,60)}${fixed.length>60?"…":""}”` });
    }
  } catch (_) { /* fail-open */ }
}

/**
 * Phase A · Staff Management Shell.
 * Ships: hero + quick-action tiles + Staff Master (list + add + basic edit + deactivate).
 * Duty roster / performance / awards / handover / documents come in Phases B-D.
 */
export default function StaffTab() {
  const [staff, setStaff] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState({ open: false, mode: "add", row: null });
  const [rosterOpen, setRosterOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [perfOpen, setPerfOpen] = useState(false);
  const [tncOpen, setTncOpen] = useState(false);

  const load = () => Promise.all([
    api.get("/staff").then(r => setStaff(r.data)).catch(e => toast.error(e.response?.data?.detail || "Load failed")),
    api.get("/ops/outlets").then(r => setOutlets(r.data)).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const filtered = staff.filter(s =>
    !q.trim() || (s.full_name || "").toLowerCase().includes(q.toLowerCase()) ||
    (s.staff_id || "").toLowerCase().includes(q.toLowerCase())
  );
  const byZone = { active: 0, inactive: 0 };
  staff.forEach(s => { if (s.status === "Active") byZone.active++; else byZone.inactive++; });

  const tiles = [
    { icon: UserPlus,     label: "Add Staff",         onClick: () => setDlg({ open: true, mode: "add", row: null }), testid: "sm-add-staff" },
    { icon: Users,        label: "Staff Master",      onClick: () => document.getElementById("staff-master-anchor")?.scrollIntoView({behavior:"smooth"}), testid: "sm-master" },
    { icon: FileText,     label: "Documents",         onClick: () => toast.info("Documents module ships in Phase B"), testid: "sm-docs" },
    { icon: Package,      label: "Handover",          onClick: () => toast.info("Handover module ships in Phase B"), testid: "sm-handover" },
    { icon: CalendarDays, label: "Duty Roster",       onClick: () => setRosterOpen(true), testid: "sm-roster" },
    { icon: ClipboardEdit,label: "Daily Reporting",   onClick: () => setReportOpen(true), testid: "sm-daily" },
    { icon: BarChart3,    label: "Performance",       onClick: () => setPerfOpen(true), testid: "sm-perf" },
    { icon: Trophy,       label: "Awards",            onClick: () => toast.info("Awards ranking ships in Phase D"), testid: "sm-awards" },
    { icon: SlidersHorizontal, label: "Parameters",   onClick: () => toast.info("Performance Parameters editor ships in Phase D"), testid: "sm-params" },
    { icon: Download,     label: "Export Excel",      onClick: () => setPerfOpen(true), testid: "sm-export" },
    { icon: ScrollText,   label: "Edit T&C",          onClick: () => setTncOpen(true), testid: "sm-tnc" },
  ];

  return (
    <div data-testid="staff-tab-root">
      <div className="rounded-2xl p-5 md:p-7 border mb-5" style={{ background: "#FFFDF6", borderColor: "#E4D6A8" }}>
        <div className="font-mono2 text-[10px] tracking-[0.4em] uppercase mb-2" style={{ color: "#B4841F" }}>Staff Management</div>
        <h2 className="font-display text-3xl md:text-4xl" style={{ color: "#2b1a0d" }}>Your people, one central master</h2>
        <p className="mt-2 text-sm max-w-2xl" style={{ color: "#5b4530" }}>
          Track joining, documents, uniform handover, duty roster and daily performance across every outlet. Admin controls granular access per module.
        </p>
        <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Total staff"       value={staff.length} />
          <Kpi label="Active"            value={byZone.active} tone="pos" />
          <Kpi label="Inactive / left"   value={byZone.inactive} tone="neg" />
          <Kpi label="Outlets"           value={outlets.length} />
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mb-5">
        {tiles.map(t => {
          const Ic = t.icon;
          return (
            <button key={t.label} data-testid={t.testid} onClick={t.onClick}
              className="flex flex-col items-center justify-center gap-2 rounded-xl border bg-white p-3 hover:bg-[#FFF8E7] transition"
              style={{ borderColor: "#E4D6A8", color: "#2b1a0d" }}>
              <Ic size={18} style={{ color: "#B4841F" }} />
              <span className="text-xs font-medium">{t.label}</span>
            </button>
          );
        })}
      </div>

      {/* Staff master */}
      <div id="staff-master-anchor" className="rounded-2xl border bg-white p-4" style={{ borderColor: "#E4D6A8" }}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <h3 className="font-display text-xl" style={{ color: "#2b1a0d" }}>Staff Master ({staff.length})</h3>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-60" />
              <Input data-testid="sm-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search name / staff ID" className="pl-8 bg-white h-9 w-56" />
            </div>
            <Button data-testid="sm-add-inline" size="sm" onClick={() => setDlg({ open: true, mode: "add", row: null })}
              className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">
              <UserPlus size={13} className="mr-1" /> Add
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-[10px] font-mono2 uppercase tracking-widest" style={{ color: "#7a6349" }}>
              <tr>
                <th className="text-left py-2">Staff ID</th>
                <th className="text-left py-2">Name</th>
                <th className="text-left py-2">Designation</th>
                <th className="text-left py-2">Outlet</th>
                <th className="text-left py-2">Mobile</th>
                <th className="text-left py-2">Status</th>
                <th className="text-right py-2 w-32"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const outletName = outlets.find(o => o.id === s.outlet_id)?.name || "—";
                return (
                  <tr key={s.id} className="border-t" style={{ borderColor: "#F3E7C4" }} data-testid={`staff-row-${s.staff_id}`}>
                    <td className="py-2 pr-2 font-mono2 text-xs">{s.staff_id}</td>
                    <td className="py-2 pr-2 font-medium" style={{color:"#2b1a0d"}}>{s.full_name}</td>
                    <td className="py-2 pr-2">{s.designation || "—"}</td>
                    <td className="py-2 pr-2">{outletName}</td>
                    <td className="py-2 pr-2">{s.mobile || "—"}</td>
                    <td className="py-2 pr-2">
                      <span className={`px-2 py-0.5 rounded-full text-xs ${s.status === "Active" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                        {s.status || "Active"}
                      </span>
                    </td>
                    <td className="py-2 pl-2 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDlg({ open: true, mode: "edit", row: s })} data-testid={`sm-edit-${s.staff_id}`} className="text-xs">Edit</Button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-xs" style={{color:"#7a6349"}}>No staff yet. Click Add Staff to get started.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <StaffDialog dlg={dlg} setDlg={setDlg} outlets={outlets} onDone={load} />
      <DutyRoster open={rosterOpen} onOpenChange={setRosterOpen} outlets={outlets} />
      <DailyReport open={reportOpen} onOpenChange={setReportOpen} outlets={outlets} />
      <PerformanceDashboard open={perfOpen} onOpenChange={setPerfOpen} outlets={outlets} />
      <TncDialog open={tncOpen} onOpenChange={setTncOpen} />
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const color = tone === "pos" ? "#0a7a3b" : tone === "neg" ? "#c0392b" : "#2b1a0d";
  return (
    <div className="rounded-xl p-3 border" style={{ borderColor: "#E4D6A8", background: "#FFF8E7" }}>
      <div className="text-[10px] font-mono2 uppercase tracking-widest" style={{color:"#7a6349"}}>{label}</div>
      <div className="font-display text-2xl" style={{ color }}>{value}</div>
    </div>
  );
}

function StaffDialog({ dlg, setDlg, outlets, onDone }) {
  const empty = { full_name: "", father_name: "", mobile: "", alt_mobile: "", dob: "", gender: "",
    designation: "", department: "", employment_type: "Full-time", salary: "", outlet_id: "",
    bank_name: "", account_number: "", ifsc: "",
    current_address: "", current_pincode: "", current_city: "", current_state: "",
    permanent_address: "", permanent_pincode: "", permanent_city: "", permanent_state: "",
    emergency_contact: "", date_of_joining: "", status: "Active" };
  const [f, setF] = useState(empty);
  const [inner, setInner] = useState("basic");
  const [docTypes, setDocTypes] = useState([]);
  const [handoverItems, setHandoverItems] = useState([]);
  const [handoverHistory, setHandoverHistory] = useState([]);
  useEffect(() => {
    setF(dlg.row ? { ...empty, ...dlg.row } : empty);
    setInner("basic");
    if (dlg.open) {
      Promise.all([
        api.get("/staff-doc-types").then(r => setDocTypes(r.data)),
        api.get("/handover-items").then(r => setHandoverItems(r.data)),
      ]).catch(() => {});
      if (dlg.mode === "edit" && dlg.row) {
        api.get(`/staff/${dlg.row.id}/handover`).then(r => setHandoverHistory(r.data)).catch(() => setHandoverHistory([]));
      }
    }
  }, [dlg]);
  const upd = (k, v) => setF(s => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.full_name.trim()) return toast.error("Full name required");
    try {
      const body = { ...f, salary: f.salary === "" ? null : Number(f.salary) };
      if (dlg.mode === "edit") await api.patch(`/staff/${dlg.row.id}`, body);
      else await api.post("/staff", body);
      toast.success(dlg.mode === "edit" ? "Staff updated" : "Staff added");
      setDlg({ open: false, mode: "add", row: null }); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deactivate = async () => {
    if (!window.confirm(`Deactivate ${dlg.row?.full_name}?`)) return;
    try { await api.post(`/staff/${dlg.row.id}/deactivate`); toast.success("Deactivated");
      setDlg({ open: false, mode: "add", row: null }); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deletePermanent = async () => {
    const name = dlg.row?.full_name || "this staff";
    const sid = dlg.row?.staff_id || "";
    const first = window.confirm(
      `⚠ PERMANENT DELETE — no traces\n\nStaff: ${sid} · ${name}\n\nThis wipes the staff record AND every trace (documents, handover, OTPs, roster entries, performance rows, audit log entries). This action CANNOT be undone.\n\nContinue?`);
    if (!first) return;
    const typed = window.prompt(`Type the staff name EXACTLY to confirm hard delete:\n\n${name}`);
    if ((typed || "").trim() !== name.trim()) { toast.error("Name did not match — cancelled"); return; }
    try {
      const r = await api.delete(`/staff/${dlg.row.id}`);
      toast.success(`Wiped ${r.data?.wiped || name} · no traces`);
      setDlg({ open: false, mode: "add", row: null }); onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const isEdit = dlg.mode === "edit";
  return (
    <Dialog open={dlg.open} onOpenChange={o => setDlg({ ...dlg, open: o })}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-3xl max-h-[90vh] overflow-y-auto" style={{ color: "#2b1a0d" }}>
        <DialogHeader><DialogTitle className="font-display text-2xl">{isEdit ? `Edit · ${dlg.row?.staff_id}` : "New Staff"}</DialogTitle></DialogHeader>
        {isEdit ? (
          <Tabs value={inner} onValueChange={setInner}>
            <TabsList className="bg-[#FFF8E7] border border-[#E4D6A8] rounded-full p-1">
              <TabsTrigger value="basic" data-testid="sd-tab-basic" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black text-xs">Basic</TabsTrigger>
              <TabsTrigger value="docs" data-testid="sd-tab-docs" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black text-xs">Documents</TabsTrigger>
              <TabsTrigger value="handover" data-testid="sd-tab-handover" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black text-xs">Handover</TabsTrigger>
              <TabsTrigger value="signatory" data-testid="sd-tab-signatory" className="rounded-full data-[state=active]:bg-[#F5C242] data-[state=active]:text-black text-xs">Signatory</TabsTrigger>
            </TabsList>
            <TabsContent value="basic" className="mt-4"><BasicSection f={f} upd={upd} outlets={outlets} /></TabsContent>
            <TabsContent value="docs" className="mt-4">
              <DocsSection staffId={dlg.row.id} docTypes={docTypes} initialDocs={dlg.row.documents || {}} onSaved={onDone} />
            </TabsContent>
            <TabsContent value="handover" className="mt-4">
              <HandoverSection staffId={dlg.row.id} items={handoverItems} history={handoverHistory} reload={() => api.get(`/staff/${dlg.row.id}/handover`).then(r => setHandoverHistory(r.data))} />
            </TabsContent>
            <TabsContent value="signatory" className="mt-4">
              <SignatorySection staffId={dlg.row.id} defaultMobile={f.mobile} signatory={dlg.row?.signatory} onDone={onDone} />
            </TabsContent>
          </Tabs>
        ) : (
          <BasicSection f={f} upd={upd} outlets={outlets} />
        )}
        <DialogFooter className="gap-2">
          {isEdit && (
            <Button variant="ghost" onClick={deletePermanent} className="text-red-700 font-semibold" data-testid="sd-delete-permanent" title="Wipe this staff and every trace">
              Delete permanently
            </Button>
          )}
          {isEdit && dlg.row?.status === "Active" && (
            <Button variant="ghost" onClick={deactivate} className="text-red-600" data-testid="sd-deactivate">Deactivate</Button>
          )}
          <Button variant="ghost" onClick={() => setDlg({ ...dlg, open: false })}>Close</Button>
          {(!isEdit || inner === "basic") && (
            <Button data-testid="sd-save" onClick={save} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">
              {isEdit ? "Save changes" : "Add staff"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BasicSection({ f, upd, outlets }) {
  return (
    <div className="grid grid-cols-2 gap-3">
      <TxtField f={f} upd={upd} k="full_name" label="Full Name *" cols={2} grammar />
      <TxtField f={f} upd={upd} k="father_name" label="Father's / Mother's Name" grammar />
      <TxtField f={f} upd={upd} k="mobile" label="Mobile Number" />
      <TxtField f={f} upd={upd} k="alt_mobile" label="Alternate Mobile" />
      <TxtField f={f} upd={upd} k="emergency_contact" label="Emergency Contact" />
      <TxtField f={f} upd={upd} k="dob" label="Date of Birth" type="date" />
      <div>
        <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Gender</label>
        <select value={f.gender || ""} onChange={e => upd("gender", e.target.value)} className="h-9 w-full px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} data-testid="sd-gender">
          <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
        </select>
      </div>
      <TxtField f={f} upd={upd} k="date_of_joining" label="Date of Joining" type="date" />
      <TxtField f={f} upd={upd} k="designation" label="Designation" grammar />
      <TxtField f={f} upd={upd} k="department" label="Department" grammar />
      <div>
        <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Employment Type</label>
        <select value={f.employment_type || "Full-time"} onChange={e => upd("employment_type", e.target.value)} className="h-9 w-full px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} data-testid="sd-emptype">
          <option>Full-time</option><option>Part-time</option><option>Contract</option><option>Trainee</option>
        </select>
      </div>
      <TxtField f={f} upd={upd} k="salary" label="Salary / CTC (₹)" type="number" />
      <div>
        <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Outlet</label>
        <select value={f.outlet_id || ""} onChange={e => upd("outlet_id", e.target.value)} className="h-9 w-full px-2 rounded border bg-white" style={{borderColor:"#E4D6A8"}} data-testid="sd-outlet">
          <option value="">— unassigned —</option>
          {outlets.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      </div>

      {/* CURRENT ADDRESS block */}
      <div className="col-span-2 mt-2 pt-3 border-t" style={{borderColor:"#F3E7C4"}}>
        <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{color:"#B4841F"}}>Current Address</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PincodeField testid="sd-current_pincode" value={f.current_pincode} onChange={v => upd("current_pincode", v)}
            onResolved={r => { upd("current_city", r.city || ""); upd("current_state", r.state || ""); }} />
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>City</label>
            <Input data-testid="sd-current_city" value={f.current_city ?? ""} onChange={e => upd("current_city", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>State</label>
            <Input data-testid="sd-current_state" value={f.current_state ?? ""} onChange={e => upd("current_state", e.target.value)} />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>
              Street / House <span className="text-[9px] normal-case tracking-normal text-[#B4841F]">· auto-fix on blur</span>
            </label>
            <Input data-testid="sd-current_address" value={f.current_address ?? ""}
              onChange={e => upd("current_address", e.target.value)}
              onBlur={e => runGrammarFix("current_address", e.target.value, upd, "address")} />
          </div>
        </div>
      </div>

      {/* PERMANENT ADDRESS block */}
      <div className="col-span-2 mt-2 pt-3 border-t" style={{borderColor:"#F3E7C4"}}>
        <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2 flex items-center justify-between" style={{color:"#B4841F"}}>
          <span>Permanent Address</span>
          <button type="button" data-testid="sd-copy-current" onClick={() => {
            upd("permanent_pincode", f.current_pincode || "");
            upd("permanent_city", f.current_city || "");
            upd("permanent_state", f.current_state || "");
            upd("permanent_address", f.current_address || "");
          }} className="text-[10px] normal-case tracking-normal text-[#B4841F] hover:underline">
            Same as current ↓
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <PincodeField testid="sd-permanent_pincode" value={f.permanent_pincode} onChange={v => upd("permanent_pincode", v)}
            onResolved={r => { upd("permanent_city", r.city || ""); upd("permanent_state", r.state || ""); }} />
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>City</label>
            <Input data-testid="sd-permanent_city" value={f.permanent_city ?? ""} onChange={e => upd("permanent_city", e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>State</label>
            <Input data-testid="sd-permanent_state" value={f.permanent_state ?? ""} onChange={e => upd("permanent_state", e.target.value)} />
          </div>
          <div className="col-span-2 md:col-span-1">
            <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>
              Street / House <span className="text-[9px] normal-case tracking-normal text-[#B4841F]">· auto-fix on blur</span>
            </label>
            <Input data-testid="sd-permanent_address" value={f.permanent_address ?? ""}
              onChange={e => upd("permanent_address", e.target.value)}
              onBlur={e => runGrammarFix("permanent_address", e.target.value, upd, "address")} />
          </div>
        </div>
      </div>

      <TxtField f={f} upd={upd} k="bank_name" label="Bank Name" />
      <TxtField f={f} upd={upd} k="account_number" label="Account Number" />
      <TxtField f={f} upd={upd} k="ifsc" label="IFSC" />
    </div>
  );
}

function DocsSection({ staffId, docTypes, initialDocs, onSaved }) {
  const [docs, setDocs] = useState(initialDocs || {});
  const [busy, setBusy] = useState({});
  const setDoc = (typeId, patch) => setDocs(d => ({ ...d, [typeId]: { ...(d[typeId] || {}), ...patch } }));
  const uploadFile = async (typeId, file) => {
    if (!file) return;
    setBusy(b => ({ ...b, [typeId]: true }));
    try {
      const fd = new FormData(); fd.append("file", file);
      const t = localStorage.getItem("cc_token");
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/uploads`, {
        method: "POST", headers: { Authorization: `Bearer ${t}` }, body: fd });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "upload failed");
      setDoc(typeId, { file_url: d.url, status: "Uploaded" });
      await api.patch(`/staff/${staffId}/documents`, { doc_type_id: typeId, status: "Uploaded", file_url: d.url });
      toast.success("Document saved");
    } catch (e) { toast.error(e.message || "Upload failed"); }
    finally { setBusy(b => ({ ...b, [typeId]: false })); }
  };
  const setStatus = async (typeId, status) => {
    setDoc(typeId, { status });
    try {
      await api.patch(`/staff/${staffId}/documents`, { doc_type_id: typeId, status,
        file_url: docs[typeId]?.file_url || "" });
      toast.success("Status saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-2 max-h-[60vh] overflow-y-auto pr-1" data-testid="docs-section">
      {docTypes.map(dt => {
        const d = docs[dt.id] || { status: "Pending" };
        return (
          <div key={dt.id} className="flex items-center gap-2 p-2 rounded border" style={{ borderColor: "#E4D6A8", background: "#FFFDF6" }}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{color:"#2b1a0d"}}>
                {dt.name}{dt.required && <span className="ml-1 text-red-600">*</span>}
              </div>
              {d.file_url && <a href={d.file_url} target="_blank" rel="noreferrer" className="text-[11px] underline" style={{color:"#B4841F"}}>View file</a>}
            </div>
            <select data-testid={`doc-status-${dt.id}`} value={d.status || "Pending"} onChange={e => setStatus(dt.id, e.target.value)}
              className={`h-8 px-2 rounded border text-xs ${d.status === "Uploaded" ? "bg-emerald-50 text-emerald-700" : d.status === "N/A" ? "bg-gray-100 text-gray-700" : "bg-amber-50 text-amber-700"}`} style={{borderColor:"#E4D6A8"}}>
              <option>Pending</option><option>Uploaded</option><option>N/A</option>
            </select>
            <label className="inline-flex items-center gap-1 cursor-pointer rounded-full px-2 h-8 text-[10px] font-mono2 uppercase tracking-widest bg-[#3B2519] text-[#F5C242]">
              <input type="file" hidden data-testid={`doc-file-${dt.id}`} onChange={e => uploadFile(dt.id, e.target.files?.[0])} />
              {busy[dt.id] ? "…" : (d.file_url ? "Replace" : "Upload")}
            </label>
          </div>
        );
      })}
      {docTypes.length === 0 && <div className="text-xs text-center py-4" style={{color:"#7a6349"}}>Loading doc types…</div>}
    </div>
  );
}

function HandoverSection({ staffId, items, history, reload }) {
  const [f, setF] = useState({ item_id: "", status: "Given", quantity: 1, size: "", issue_date: new Date().toISOString().slice(0,10), remarks: "" });
  const save = async () => {
    if (!f.item_id) return toast.error("Pick an item");
    try {
      await api.post(`/staff/${staffId}/handover`, { ...f, quantity: Number(f.quantity) || 1 });
      toast.success("Handover recorded");
      setF({ item_id: "", status: "Given", quantity: 1, size: "", issue_date: new Date().toISOString().slice(0,10), remarks: "" });
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="space-y-4" data-testid="handover-section">
      <div className="rounded-lg border p-3" style={{ borderColor: "#E4D6A8", background: "#FFFDF6" }}>
        <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{ color: "#B4841F" }}>Record new handover</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          <select data-testid="ho-item" value={f.item_id} onChange={e => setF({...f, item_id: e.target.value})} className="h-9 px-2 rounded border bg-white text-sm" style={{borderColor:"#E4D6A8"}}>
            <option value="">— item —</option>
            {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
          </select>
          <select data-testid="ho-status" value={f.status} onChange={e => setF({...f, status: e.target.value})} className="h-9 px-2 rounded border bg-white text-sm" style={{borderColor:"#E4D6A8"}}>
            <option>Given</option><option>Pending</option><option>Not Given</option>
          </select>
          <Input data-testid="ho-qty" type="number" min="1" placeholder="Qty" value={f.quantity} onChange={e => setF({...f, quantity: e.target.value})} className="h-9" />
          <Input data-testid="ho-size" placeholder="Size (M / L / XL)" value={f.size} onChange={e => setF({...f, size: e.target.value})} className="h-9" />
          <Input data-testid="ho-date" type="date" value={f.issue_date} onChange={e => setF({...f, issue_date: e.target.value})} className="h-9" />
          <Input data-testid="ho-remarks" placeholder="Remarks" value={f.remarks} onChange={e => setF({...f, remarks: e.target.value})} className="h-9" />
        </div>
        <Button data-testid="ho-save" size="sm" onClick={save} className="mt-3 rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">
          <Check size={13} className="mr-1" /> Record handover
        </Button>
      </div>
      <div>
        <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2" style={{ color: "#7a6349" }}>History ({history.length})</div>
        {history.length === 0 ? (
          <div className="text-xs text-center py-3" style={{color:"#7a6349"}}>No handover entries yet.</div>
        ) : (
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {history.slice().reverse().map(h => (
              <div key={h.id} className="text-xs p-2 rounded border flex items-center gap-2" style={{ borderColor: "#F3E7C4", background: "#fff" }} data-testid={`ho-row-${h.id}`}>
                <b>{h.item_name}</b>
                <span className={`px-2 py-0.5 rounded-full text-[10px] ${h.status === "Given" ? "bg-emerald-100 text-emerald-700" : h.status === "Pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{h.status}</span>
                <span className="opacity-60">Qty {h.quantity}{h.size && ` · ${h.size}`}{h.issue_date && ` · ${h.issue_date}`}</span>
                {h.remarks && <span className="opacity-70 truncate">· {h.remarks}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------
// Pincode → City/State auto-lookup (postalpincode.in via backend)
// ------------------------------------------------------------
function PincodeField({ value, onChange, onResolved, testid }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    const v = (value || "").trim();
    if (!/^\d{6}$/.test(v)) { setErr(""); return; }
    let cancelled = false;
    setBusy(true); setErr("");
    api.get(`/pincode/${v}`)
      .then(r => { if (!cancelled) { onResolved?.(r.data); } })
      .catch(e => { if (!cancelled) setErr(e.response?.data?.detail || "Pincode not found"); })
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [value]);  // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div>
      <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>
        Pincode
        {busy && <span className="ml-1 text-[9px] normal-case tracking-normal text-[#B4841F]">· fetching…</span>}
        {err && <span className="ml-1 text-[9px] normal-case tracking-normal text-red-600">· {err}</span>}
      </label>
      <Input data-testid={testid} inputMode="numeric" maxLength={6} value={value ?? ""}
        onChange={e => onChange(e.target.value.replace(/\D/g,"").slice(0,6))}
        placeholder="6-digit"/>
    </div>
  );
}

// ------------------------------------------------------------
// Signatory OTP + T&C acknowledgement (per-staff, on edit)
// ------------------------------------------------------------
function SignatorySection({ staffId, defaultMobile, signatory, onDone }) {
  const [tnc, setTnc] = useState({ text: "", version: 0 });
  const [mobile, setMobile] = useState(defaultMobile || "");
  const [signed, setSigned] = useState(signatory?.signed ? signatory : null);
  const [step, setStep] = useState(signatory?.signed ? "done" : "start");
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);
  useEffect(() => { api.get("/staff-tnc").then(r => setTnc(r.data)).catch(()=>{}); }, []);
  const sendOtp = async () => {
    if (!/^\d{10}$/.test((mobile||"").replace(/\D/g,""))) return toast.error("Enter 10-digit mobile");
    if (!agreed) return toast.error("Staff must agree to T&C before OTP");
    setBusy(true);
    try {
      const r = await api.post("/staff/signatory-otp/send", { staff_id: staffId, mobile });
      setDevCode(r.data?.dev_code || "");
      setStep("verify");
      toast.success(`OTP sent to ****${r.data?.mobile_last4}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    if (!/^\d{4,8}$/.test(code)) return toast.error("Enter the code");
    setBusy(true);
    try {
      const r = await api.post("/staff/signatory-otp/verify", { staff_id: staffId, code, tnc_version: tnc.version || 0 });
      toast.success("Signatory verified");
      setSigned(r.data);
      setStep("done"); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="signatory-section">
      <div className="rounded-lg border p-3" style={{borderColor:"#E4D6A8", background:"#FFFDF6"}}>
        <div className="text-[10px] font-mono2 uppercase tracking-widest mb-2 flex items-center gap-2" style={{color:"#B4841F"}}>
          <ShieldCheck size={12}/> Terms & Conditions <span className="text-[9px] text-[#7a6349]">v{tnc.version || 0}</span>
        </div>
        <div className="max-h-40 overflow-y-auto text-xs whitespace-pre-wrap p-2 rounded border" style={{borderColor:"#F3E7C4", background:"#fff", color:"#3B2519"}} data-testid="sig-tnc-body">
          {tnc.text || "— T&C not yet configured. Ask admin to configure via Staff → Edit T&C."}
        </div>
      </div>
      {step === "done" ? (
        <div className="rounded-lg border p-3 text-sm" style={{borderColor:"#a7e0b8", background:"#eefbf1", color:"#0a7a3b"}} data-testid="sig-done">
          <div className="flex items-center gap-2 font-medium"><Check size={14}/> T&C signed by staff</div>
          <div className="text-xs mt-1 text-[#3B2519] opacity-80">
            v{signed?.tnc_version} · mobile ****{signed?.mobile_last4} · attested by {signed?.attested_by} · {signed?.signed_at?.slice(0,16)?.replace('T',' ')}
          </div>
        </div>
      ) : step === "start" ? (
        <div className="space-y-2">
          <label className="flex items-start gap-2 text-xs" style={{color:"#3B2519"}}>
            <input type="checkbox" data-testid="sig-agree" checked={agreed} onChange={e=>setAgreed(e.target.checked)} className="mt-1"/>
            I have read the above Terms & Conditions and I agree.
          </label>
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>Staff Mobile (for OTP)</label>
              <Input data-testid="sig-mobile" inputMode="numeric" maxLength={10} value={mobile}
                onChange={e => setMobile(e.target.value.replace(/\D/g,"").slice(0,10))} placeholder="10-digit"/>
            </div>
            <Button data-testid="sig-send" disabled={busy} onClick={sendOtp} className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">
              {busy ? "Sending…" : "Send OTP"}
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {devCode && (
            <div className="text-[11px] rounded p-2 border" style={{borderColor:"#F3E7C4", background:"#FFF8E7", color:"#B4841F"}} data-testid="sig-devcode">
              <b>DEV MODE</b> · Read this to staff: <span className="font-mono font-bold text-black">{devCode}</span> · Twilio SMS wire-up pending
            </div>
          )}
          <div className="flex gap-2 items-end">
            <div className="flex-1">
              <label className="text-[10px] font-mono2 uppercase tracking-widest block mb-1" style={{color:"#7a6349"}}>OTP Code</label>
              <Input data-testid="sig-code" inputMode="numeric" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g,"").slice(0,6))} placeholder="6-digit"/>
            </div>
            <Button data-testid="sig-verify" disabled={busy} onClick={verify} className="rounded-full bg-[#F5C242] text-black hover:bg-[#e9a722]">
              {busy ? "Verifying…" : "Verify & Sign"}
            </Button>
            <Button variant="ghost" onClick={() => { setStep("start"); setCode(""); setDevCode(""); }} data-testid="sig-back" className="text-xs">Back</Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Admin: T&C editor (used across all staff joining signatories)
// ------------------------------------------------------------
function TncDialog({ open, onOpenChange }) {
  const [text, setText] = useState("");
  const [version, setVersion] = useState(0);
  const [busy, setBusy] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  useEffect(() => {
    if (!open) return;
    api.get("/staff-tnc").then(r => {
      setText(r.data?.text || ""); setVersion(r.data?.version || 0);
      setUpdatedAt(r.data?.updated_at || "");
    }).catch(() => {});
  }, [open]);
  const save = async () => {
    if (!text.trim()) return toast.error("T&C text required");
    setBusy(true);
    try {
      const r = await api.post("/staff-tnc", { text });
      setVersion(r.data.version); setUpdatedAt(r.data.updated_at);
      toast.success(`T&C v${r.data.version} saved`);
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white border-[#E4D6A8] max-w-3xl" style={{ color: "#2b1a0d" }}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <ScrollText size={20} style={{color:"#B4841F"}}/> Staff Terms & Conditions
            <span className="text-xs font-mono2 text-[#7a6349]">v{version}{updatedAt && ` · updated ${updatedAt.slice(0,10)}`}</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-[#5b4530]">
            This is the text every new joinee sees on the Signatory step. Every save bumps the version and is logged.
          </div>
          <textarea data-testid="tnc-text" value={text} onChange={e => setText(e.target.value)}
            className="w-full min-h-[300px] p-3 rounded border font-mono text-sm"
            style={{borderColor:"#E4D6A8", background:"#FFFDF6"}}
            placeholder="Enter the full Terms & Conditions the staff must accept during joining…" />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
          <Button data-testid="tnc-save" onClick={save} disabled={busy} className="bg-[#F5C242] text-black hover:bg-[#e9a722]">
            {busy ? "Saving…" : `Save as v${version + 1}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

